import { formatLocalPhone } from '@rentspree/helper'
import PropTypes from 'prop-types'

import { Div, ImageIcon, Span, Title } from 'components/common/atoms'
import { List } from 'components/common/molecules'
import { STATIC_FILES_CDN_URL } from 'constant'
import { COLOR } from 'styles/constants'

import { ContactInformationWrapper, SocialMedias, TradeMarkWrapper } from './style'

type ContactRentSpreeProps = {
  weekdayTime: string
  weekendTime: string
  phone: string
  email: string
  socialContacts: SocialContact[]
} & IElement

type ContactInformation = {
  icon: string
  testId: string
  text: React.ReactNode
  href?: string
}

export const ContactRentSpree: React.FC<ContactRentSpreeProps> = ({
  weekdayTime,
  weekendTime,
  phone,
  email,
  socialContacts,
  ...props
}) => {
  const textStyle = {
    $weight: '400',
    $color: 'white',
    $mSize: '14px',
    $size: '16px',
  }
  const dataSource: ContactInformation[] = [
    {
      icon: `${STATIC_FILES_CDN_URL}footer-time.svg`,
      testId: 'footer-time',
      text: (
        <Div>
          <Span {...textStyle}>
            {weekdayTime}
            <br />
            {weekendTime}
          </Span>
        </Div>
      ),
    },
    {
      icon: `${STATIC_FILES_CDN_URL}footer-phone.svg`,
      testId: 'footer-phone',
      text: (
        <Div>
          <Span {...textStyle}>{formatLocalPhone(phone)}</Span>
        </Div>
      ),
      href: `tel:${phone}`,
    },
    {
      icon: `${STATIC_FILES_CDN_URL}footer-mail.svg`,
      testId: 'footer-mail',
      text: (
        <Div>
          <Span {...textStyle}>{email}</Span>
        </Div>
      ),
      href: `mailto:${email}`,
    },
  ]

  const wrapWithLink = (element: React.ReactNode, href?: string, testId?: string) =>
    href ? (
      <a data-testid={testId} href={href}>
        {element}
      </a>
    ) : (
      element
    )

  const renderItem = ({ icon, testId, text, href }: ContactInformation): React.ReactNode => (
    <>
      {wrapWithLink(
        <ContactInformationWrapper>
          <Div
            style={{
              width: '24px',
              height: '24px',
              background: `url(${icon}) no-repeat center center`,
            }}
          />
          {text}
        </ContactInformationWrapper>,
        href,
        `contact-link-${testId}`,
      )}
    </>
  )

  return (
    <Div {...props}>
      <Title
        $color={COLOR.MINT_GREEN_LV3}
        $size="32px"
        $mSize="22px"
        $lineHeight="48px"
        $mLineHeight="32px"
        $weight="700"
        $margin="0 0 16px 0"
      >
        Have a question?
      </Title>
      <List dataSource={dataSource} renderItem={renderItem} />
      <SocialMedias>
        {socialContacts.map(({ link, img }) => (
          <a href={link} target="_blank" rel="noreferrer" data-testid={`social-link-${img}`}>
            <ImageIcon src={`${img}`} />
          </a>
        ))}
      </SocialMedias>
      <TradeMarkWrapper $mt="32" style={{ lineHeight: '40px' }}>
        <Span {...textStyle}>&copy;RentSpree, Inc.</Span>
      </TradeMarkWrapper>
      {/* <TradeMarkWrapper style={{ lineHeight: '40px' }}>
        <Span {...textStyle}>Do Not Sell My Personal Information</Span>
      </TradeMarkWrapper> */}
    </Div>
  )
}

ContactRentSpree.propTypes = {
  weekdayTime: PropTypes.string.isRequired,
  weekendTime: PropTypes.string.isRequired,
  phone: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  socialContacts: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string.isRequired,
      img: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
}
